import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";

import { Table, FrameBox, Labelbox } from "../../components";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button as MuiButton,
  Box,
  Paper,
  Avatar,
  Divider,
  TextField,
} from "@material-ui/core";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ComposedChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import { useSelector, useDispatch } from "react-redux";
import { CLIENT_ACTION } from "../../redux/action";
import { CONTRACT_ACTION } from "../../redux/action";
import { EMPLOYEE_ACTION } from "../../redux/action";
import { INVOICE_ACTION } from "../../redux/action";
import { TIMESHEET_ACTION } from "../../redux/action";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  dateFormatter,
  getNodeText,
  getUserRole,
  Toaster,
} from "../../utils/common";
import {
  formatInvoicePaidDateDisplay,
  getInvoicePaymentMethodLabel,
} from "../../utils/invoiceStatusDisplay";
import * as FileSaver from "file-saver";
import { utils, write } from "xlsx";
import _JSON from "./structure";
import { Invoice, Handshake, RegisterClient, Payment, Timesheet } from "../../images";
import {
  TIMESHEET_REPORT_KEY,
  aggregateTimesheetDetails,
  filterTimesheetsByManager,
  mapApiTimesheetReportRows,
  buildManagerAccordionTimesheetRows,
  filterManagerAccordionRows,
} from "./timesheetReportUtils";
import {
  REVENUE_BY_EMPLOYEE_REPORT_KEY,
  buildManagerAccordionRevenueRows,
} from "./revenueReportUtils";
import {
  openRevenueCustomersTab,
  openRevenuePaidInvoicesTab,
  openRevenuePendingInvoicesTab,
} from "./revenuePendingInvoices";
import ReportsManagerAccordionTable from "./ReportsManagerAccordionTable";
import {
  appendManagerReportFilters,
  buildTeamScope,
  collectDescendantEmployeeIds,
  filterForTeam,
} from "./reportsTeamScope";
import {
  createDateFilterState,
  DEFAULT_DATE_RANGE_PRESET,
  getReportDateRange,
} from "../../utils/reportDateRanges";
import { makeStyles } from "@material-ui/core/styles";
import "./reports.scss";
import { GetApp } from "@material-ui/icons";
import { use } from "react";
import DownloadIcon from "@material-ui/icons/GetApp";

import {
  Chart as ChartJS,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  ArcElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  Title,
  ChartTooltip,
  ChartLegend,
  ArcElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
);

const useStyles = makeStyles((theme) => ({
  gridItem: {
    [theme.breakpoints.up("md")]: {
      maxWidth: "20%",
      flexBasis: "20%",
    },
    // [theme.breakpoints.only("sm")]: {
    //   maxWidth: "33.3%",
    //   flexBasis: "33.3%",
    // },
  },
}));

const TIMESHEET_ACCORDION_COLUMNS = [
  { id: "billable_hours", label: "Billable Hours", width: "18%" },
  { id: "non_billable_hours", label: "Non-Billable Hours", width: "18%" },
  { id: "idle_hours", label: "Idle Time", width: "18%" },
  { id: "total_hours", label: "Total Hours", width: "18%" },
];

const REVENUE_ACCORDION_COLUMNS = [
  { id: "num_customers", label: "No. of Customers", width: "14%" },
  { id: "num_payments_done", label: "No. of Payments Done", width: "14%" },
  { id: "num_pending_invoices", label: "No. of Pending Invoices", width: "14%" },
  { id: "amount_collected", label: "Amount Paid", width: "14%" },
  { id: "amount_pending", label: "Amount Pending", width: "14%" },
];

// Custom Tooltip Component
const CustomRevenueTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          border: "none",
          borderRadius: "12px",
          boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
          padding: "12px 16px",
        }}
      >
        {/* Label in black */}
        <div
          style={{
            color: "#505050",
            fontWeight: 600,
            marginBottom: "8px",
            fontSize: "14px",
          }}
        >
          {label}
        </div>
        {/* Payload items with colors */}
        {payload.map((entry, index) => {
          // Check dataKey for revenue charts or label for invoice charts
          const isCollected =
            entry.dataKey === "collected" ||
            label?.toLowerCase().includes("paid");
          const color = isCollected ? "#22c55e" : "#ef4444";
          const entryLabel = isCollected ? "Paid" : "Pending";
          return (
            <div
              key={index}
              style={{
                color: color,
                fontWeight: 500,
                fontSize: "13px",
                marginBottom: index < payload.length - 1 ? "4px" : "0px",
              }}
            >
              {`${entryLabel}: ${formatCurrency(entry.value)}`}
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

const CustomTimesheetTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const seriesLabels = {
    billable: "Billable",
    nonBillable: "Non-Billable",
    idle: "Idle",
  };
  const seriesColors = {
    billable: "#1C74BC",
    nonBillable: "#f59e0b",
    idle: "#94a3b8",
  };

  const totalHours = payload.reduce((sum, entry) => sum + Number(entry.value || 0), 0);

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
        padding: "12px 16px",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          color: "#1f2937",
          fontWeight: 600,
          marginBottom: 8,
          fontSize: 14,
        }}
      >
        {label}
      </div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{
            color: seriesColors[entry.dataKey] || "#64748b",
            fontWeight: 500,
            fontSize: 13,
            marginBottom: 4,
          }}
        >
          {`${seriesLabels[entry.dataKey] || entry.name}: ${Number(entry.value).toFixed(1)} hrs`}
        </div>
      ))}
      <div
        style={{
          borderTop: "1px solid #e5e7eb",
          marginTop: 8,
          paddingTop: 8,
          color: "#324055ff",
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        {`Total Hours: ${totalHours.toFixed(1)} hrs`}
      </div>
    </div>
  );
};

const getInvoiceStatusLabel = (status) => {
  const statusValue = Number(status);
  if (statusValue === 4) return "Cancelled";
  if (statusValue === 2) return "Partially Paid";
  if (statusValue === 1 || statusValue === 3) return "Paid";
  return "Pending";
};

const getCollectionReportPendingAmount = (invoice) => {
  const { paidAmount, dueAmount, totalAmount } = getInvoiceAmounts(invoice);
  return dueAmount > 0 ? dueAmount : Math.max(totalAmount - paidAmount, 0);
};

const getCollectionReportStatusLabel = (invoice) => {
  const statusLabel = getInvoiceStatusLabel(invoice?.status);
  const pendingAmount = getCollectionReportPendingAmount(invoice);
  if (statusLabel === "Partially Paid" && pendingAmount <= 0) return "Paid";
  if (statusLabel === "Paid" && pendingAmount > 0) {
    const { paidAmount } = getInvoiceAmounts(invoice);
    return paidAmount > 0 ? "Partially Paid" : "Pending";
  }
  return statusLabel;
};

const parseAmount = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = String(value)
    .replace(/[$,\s]/g, "")
    .trim();
  if (!normalized || normalized.toLowerCase() === "null") return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};
const formatCurrency = (value) => {
  const amount = parseAmount(value) ?? 0;
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
const formatCompactCurrency = (value) => {
  const amount = parseAmount(value) ?? 0;
  if (!amount) return "$0";
  return `$${amount.toLocaleString("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  })}`;
};

const formatRevenueCurrencyCell = (columnId, value) => {
  if (columnId === "amount_collected" || columnId === "amount_pending") {
    return formatCurrency(value);
  }
  return value ?? 0;
};

const getInvoiceAmounts = (invoice) => {
  const paidAmount =
    parseAmount(invoice?.amount_paid) ??
    parseAmount(invoice?.amount_collected) ??
    0;
  const totalAmountFromApi = parseAmount(invoice?.total) ?? 0;
  const dueFromApi =
    parseAmount(invoice?.amount_due) ??
    parseAmount(invoice?.due_amount) ??
    parseAmount(invoice?.amount_pending);
  const dueAmount = Number.isFinite(dueFromApi) ? dueFromApi : 0;
  const totalAmount = totalAmountFromApi || paidAmount + dueAmount;

  return {
    paidAmount,
    dueAmount,
    totalAmount,
  };
};

const CustomInvoiceTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0]?.payload || {};
  const isPartiallyPaid = (point?.name || "")
    .toLowerCase()
    .includes("partially");
  const isCancelled = (point?.name || "").toLowerCase() === "cancelled";
  const isPaid =
    (point?.name || "").toLowerCase().includes("paid") && !isPartiallyPaid;

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "none",
        borderRadius: "12px",
        boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
        padding: "12px 16px",
      }}
    >
      <div
        style={{
          color: "#505050",
          fontWeight: 600,
          marginBottom: "8px",
          fontSize: "14px",
        }}
      >
        {label}
      </div>
      {isPartiallyPaid ? (
        <>
          <div
            style={{ color: "#1d4ed8", fontSize: "13px", marginBottom: "4px" }}
          >
            Partial Paid: {formatCurrency(point.partialPaidAmount)}
          </div>
          <div style={{ color: "#dc2626", fontSize: "13px" }}>
            Partial Pending: {formatCurrency(point.partialPendingAmount)}
          </div>
        </>
      ) : (
        <div
          style={{
            color: isCancelled ? "#b42318" : isPaid ? "#16a34a" : "#dc2626",
            fontSize: "13px",
          }}
        >
          Amount:{" "}
          {formatCurrency(
            isCancelled
              ? point.displayCancelledAmount
              : isPaid
                ? point.displayPaidAmount
                : point.displayPendingAmount,
          )}
        </div>
      )}
    </div>
  );
};

/** Export report table data to Excel. Uses header id when present on row, else column index (row keys order). */
function exportReportTableToExcel(header, data, fileName) {
  if (!header?.length || !Array.isArray(data)) return;
  const skipKeys = new Set(["enabledActions", "notAvail"]);
  const exportCols = header.filter(
    (h) => h.id && h.label !== "Action" && !skipKeys.has(h.id)
  );
  if (!exportCols.length) return;
  const orderedKeys = data.length
    ? Object.keys(data[0]).filter((k) => !skipKeys.has(k))
    : [];
  const headerRow = exportCols.map((c) => c.label);
  const dataRows = data.map((row) =>
    exportCols.map((col, i) => {
      let val = row[col.id];
      if (val === undefined || val === null) {
        const keyByPos = orderedKeys[i];
        if (keyByPos !== undefined) val = row[keyByPos];
      }
      const text = getNodeText(val);
      return text !== undefined && text !== null ? String(text).trim() : "";
    })
  );
  const aoa = [headerRow, ...dataRows];
  const ws = utils.aoa_to_sheet(aoa);
  const wb = { Sheets: { Export: ws }, SheetNames: ["Export"] };
  const excelBuffer = write(wb, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });
  const safeName = (fileName || "report")
    .replace(/\s+/g, "_")
    .replace(/[<>:"/\\|?*]/g, "");
  FileSaver.saveAs(
    new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${safeName}.xlsx`
  );
  Toaster.success("Exported to Excel successfully");
}

const COLLECTION_INVOICE_EXPORT_HEADERS = [
  { id: "createdAt", label: "Date" },
  { id: "bill_to_name", label: "Client Name" },
  { id: "invoice_no", label: "Invoice No." },
  { id: "bill_from_name", label: "Handled By" },
  { id: "total", label: "Invoice Amount" },
  { id: "paid_amount", label: "Paid Amount" },
  { id: "pending_amount", label: "Pending Amount" },
  { id: "status", label: "Invoice Status" },
  { id: "paid_date", label: "Paid Date" },
  { id: "payment_method", label: "Payment Method" },
];

function exportCollectionInvoiceReport(rows, fileName) {
  const sorted = [...(rows || [])].sort((a, b) => {
    const aTime = Date.parse(a?.createdAt);
    const bTime = Date.parse(b?.createdAt);
    const aValue = Number.isNaN(aTime) ? 0 : aTime;
    const bValue = Number.isNaN(bTime) ? 0 : bTime;
    return aValue - bValue;
  });
  exportReportTableToExcel(
    COLLECTION_INVOICE_EXPORT_HEADERS,
    sorted,
    fileName,
  );
}

const REPORT_CATEGORY_ICONS = {
  "Sign Up & Registration": RegisterClient,
  Invoice: Invoice,
  "Payments & Revenue": Payment,
  Clients: Handshake,
  Timesheet: Timesheet,
};

function getManagerIdForReports(isManagerView, loggedManagerId, selectedManager) {
  if (isManagerView && loggedManagerId) return loggedManagerId;
  return selectedManager !== "All" ? selectedManager : null;
}

function getManagerKeyForEmployeeList(
  isManagerView,
  loggedManagerId,
  selectedManager,
) {
  if (isManagerView && loggedManagerId) return loggedManagerId;
  return selectedManager;
}

function getDateFilterPresetForReport(reportKey) {
  return reportKey === TIMESHEET_REPORT_KEY ? "All" : DEFAULT_DATE_RANGE_PRESET;
}

function buildReportDateFilterState(reportKey) {
  return createDateFilterState(getDateFilterPresetForReport(reportKey));
}

function appendReportDateFilters(filters, dateFilter) {
  if (dateFilter?.start_date) filters.start_date = dateFilter.start_date;
  if (dateFilter?.end_date) filters.end_date = dateFilter.end_date;
  return filters;
}

const defaultYearDates = getReportDateRange(DEFAULT_DATE_RANGE_PRESET);

const Reports = () => {
  const tableExportRef = useRef(null);
  const classes = useStyles();
  const dispatch = useDispatch();
  const isManagerView = getUserRole() === "manager";
  const loggedManagerId = localStorage.getItem("user_id") || "";
  const STRUCTURE_JSON = { ..._JSON };
  const formattedDate = new Date().toISOString().split("T")[0];
  const [registrationFilters, setRegistrationFilters] = useState({
    start_date: null,
    end_date: null,
  });
  const [invoiceFilters, setInvoiceFilters] = useState({
    start_date: null,
    end_date: null,
  });
  const [collectionStatusFilter, setCollectionStatusFilter] = useState("All");
  const [revenueInvoiceFilters, setRevenueInvoiceFilters] = useState({
    start_date: defaultYearDates.start_date,
    end_date: defaultYearDates.end_date,
    manager_id: null,
    employee_id: null,
  });
  const [contractFilters, setContractFilters] = useState({
    start_date: null,
    end_date: null,
  });
  const [clientFiltersAll, setClientFiltersAll] = useState({
    start_date: null,
    end_date: null,
  });
  const [clientFiltersIndividual, setClientFiltersIndividual] = useState({
    start_date: null,
    end_date: null,
  });
  const [clientFiltersBusiness, setClientFiltersBusiness] = useState({
    start_date: null,
    end_date: null,
  });
  const [revenueTimeWiseFilters, setRevenueTimeWiseFilter] = useState({
    start_date: null,
    end_date: null,
  });

  const dateRanges = [
    "All",
    "Today",
    "This Week",
    "Last Week",
    "This Month",
    "Last Month",
    "This Quarter",
    "Last Quarter",
    "This Year",
    "Last Year",
  ];

  const getAllReportKeys = () => {
    let reportKeys = new Set();
    Object.values(STRUCTURE_JSON).forEach((reports) => {
      reports.forEach((report) => {
        if (report.label) {
          reportKeys.add(report.label);
        }
        if (report.key) {
          reportKeys.add(report.key);
        }
      });
    });
    reportKeys.add("Revenue by Associate (Employee)");
    return Array.from(reportKeys);
  };

  const initialDateFilters = getAllReportKeys().reduce((acc, key) => {
    acc[key] = buildReportDateFilterState(key);
    return acc;
  }, {});

  const [dateFilters, setDateFilters] = useState(initialDateFilters);

  console.log("Initialized dateFilters with keys:", Object.keys(dateFilters));

  const getDateRange = (filter) => {
    // Helper to format date without timezone issues
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // ✅ Default should be ALL (no date filter)
    if (!filter || filter === "All") {
      return { start_date: null, end_date: null };
    }

    const today = new Date();
    let startDate, endDate;

    switch (filter) {
      case "Today":
        startDate = new Date(today);
        endDate = new Date(today);
        break;

      case "This Week":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay());
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        break;

      case "Last Week":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay() - 7);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        break;

      case "This Month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;

      case "Last Month":
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;

      case "This Quarter":
        const thisQuarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
        startDate = new Date(today.getFullYear(), thisQuarterStartMonth, 1);
        endDate = new Date(today.getFullYear(), thisQuarterStartMonth + 3, 0);
        break;

      case "Last Quarter":
        let lastQuarterStartMonth = Math.floor(today.getMonth() / 3) * 3 - 3;
        let lastQuarterYear = today.getFullYear();

        if (lastQuarterStartMonth < 0) {
          lastQuarterStartMonth += 12;
          lastQuarterYear -= 1;
        }

        startDate = new Date(lastQuarterYear, lastQuarterStartMonth, 1);
        endDate = new Date(lastQuarterYear, lastQuarterStartMonth + 3, 0);
        break;

      case "This Year":
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31);
        break;

      case "Last Year":
        startDate = new Date(today.getFullYear() - 1, 0, 1);
        endDate = new Date(today.getFullYear() - 1, 11, 31);
        break;

      default:
        return { start_date: null, end_date: null };
    }

    return {
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
    };
  };

  const handleDateRangeChange = (range, reportKey, actionType) => {
    if (!dateFilters[reportKey]) {
      console.warn(`Report key '${reportKey}' not found in dateFilters.`);
      return;
    }

    // ✅ ✅ CASE 1: When user selects "All"
    if (range === "All") {
      setDateFilters((prev) => ({
        ...prev,
        [reportKey]: {
          range: "All",
          start_date: null,
          end_date: null,
        },
      }));

      const filters = {};

      // ✅ Keep your existing special Revenue logic
      if (reportKey === "Revenue by Associate (Employee)") {
        Object.assign(
          filters,
          appendManagerReportFilters(
            {
              employee_id:
                selectedEmployee !== "All" ? selectedEmployee : null,
            },
            isManagerView,
            getManagerIdForReports(
              isManagerView,
              loggedManagerId,
              selectedManager,
            ),
            { revenueByEmployee: true },
          ),
        );
      }
      if (reportKey === TIMESHEET_REPORT_KEY) {
        Object.assign(
          filters,
          appendManagerReportFilters(
            {
              employee_id:
                selectedEmployee !== "All" ? selectedEmployee : null,
            },
            isManagerView,
            getManagerIdForReports(
              isManagerView,
              loggedManagerId,
              selectedManager,
            ),
          ),
        );
      }

      dispatch(actionType(filters)); // ✅ Fetch ALL data again
      return;
    }

    // ✅ ✅ CASE 2: All other date ranges (Today, This Week, etc.)
    const { start_date, end_date } = getDateRange(range);

    setDateFilters((prev) => ({
      ...prev,
      [reportKey]: {
        range,
        start_date,
        end_date,
      },
    }));

    const filters = {};
    if (start_date) filters.start_date = start_date;
    if (end_date) filters.end_date = end_date;

    if (reportKey === "Revenue by Associate (Employee)") {
      Object.assign(
        filters,
        appendManagerReportFilters(
          {
            employee_id: selectedEmployee !== "All" ? selectedEmployee : null,
          },
          isManagerView,
          getManagerIdForReports(
            isManagerView,
            loggedManagerId,
            selectedManager,
          ),
          { revenueByEmployee: true },
        ),
      );
    }
    if (reportKey === TIMESHEET_REPORT_KEY) {
      Object.assign(
        filters,
        appendManagerReportFilters(
          {
            employee_id: selectedEmployee !== "All" ? selectedEmployee : null,
          },
          isManagerView,
          getManagerIdForReports(
            isManagerView,
            loggedManagerId,
            selectedManager,
          ),
        ),
      );
    }

    dispatch(actionType(filters));
  };

  // const getDateRange = (filter) => {
  //   if (!filter) return { start_date: formattedDate, end_date: formattedDate };

  //   const today = new Date();
  //   let startDate, endDate = new Date(today);

  //   switch (filter) {
  //     case "Today":
  //       startDate = new Date(today);
  //       break;
  //     case "This Week":
  //       startDate = new Date(today);
  //       startDate.setDate(today.getDate() - today.getDay());
  //       break;
  //     case "Last Week":
  //       startDate = new Date(today);
  //       startDate.setDate(today.getDate() - today.getDay() - 7);
  //       endDate = new Date(startDate);
  //       endDate.setDate(startDate.getDate() + 6);
  //       break;
  //     case "This Month":
  //       startDate = new Date(today.getFullYear(), today.getMonth(), 1);
  //       break;
  //     case "Last Month":
  //       startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  //       endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  //       break;
  //     case "This Quarter":
  //       startDate = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
  //       break;
  //     case "Last Quarter":
  //       let lastQuarterStartMonth = Math.floor(today.getMonth() / 3) * 3 - 3;
  //       if (lastQuarterStartMonth < 0) {
  //         lastQuarterStartMonth = 9;
  //         startDate = new Date(today.getFullYear() - 1, lastQuarterStartMonth, 1);
  //       } else {
  //         startDate = new Date(today.getFullYear(), lastQuarterStartMonth, 1);
  //       }
  //       endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0);
  //       break;
  //     case "This Year":
  //       startDate = new Date(today.getFullYear(), 0, 1);
  //       break;
  //     case "Last Year":
  //       startDate = new Date(today.getFullYear() - 1, 0, 1);
  //       endDate = new Date(today.getFullYear() - 1, 11, 31);
  //       break;
  //     default:
  //       startDate = new Date(today);
  //   }

  //   return {
  //     start_date: startDate.toISOString().split("T")[0],
  //     end_date: endDate.toISOString().split("T")[0],
  //   };
  // };

  // const handleDateRangeChange = (range, reportKey, actionType) => {
  //   const { start_date, end_date } = getDateRange(range);
  //   setDateFilters((prev) => ({
  //     ...prev,
  //     [reportKey]: { range, start_date, end_date },
  //   }));

  //   const filters = { start_date, end_date };
  //   dispatch(actionType(filters));
  // };

  // const reports = [
  //   { id: "registration", label: "Registration Reports", api: CLIENT_ACTION.getOverAllClients, icons: RegisterClient },
  //   { id: "invoices", label: "Collection/Invoice Report", api: INVOICE_ACTION.getAllInvoices, icons: Invoice },
  //   { id: "revenue", label: "Revenue by Associate (Employee)", api: INVOICE_ACTION.getAllInvoices, icons: Payment },
  //   { id: "clientsAll", label: "Client List - All Clients", api: CLIENT_ACTION.getOverAllClients, icons: Handshake },
  //   { id: "clientsIndividual", label: "Client List - Individual", api: CLIENT_ACTION.getIndividualClients, icons: RegisterClient },
  //   { id: "clientsBusiness", label: "Client List - Business", api: CLIENT_ACTION.getBusinessClients, icons: Invoice },
  // ];

  const [lineChartData, setLineChartData] = useState({});
  const [pieChartData1, setPieChartData1] = useState({});
  const [pieChartData2, setPieChartData2] = useState({});

  // Graph data for each tab
  const [registrationChartData, setRegistrationChartData] = useState([]);
  const [invoiceChartData, setInvoiceChartData] = useState([]);
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [clientChartData, setClientChartData] = useState([]);
  const [registrationPeriod, setRegistrationPeriod] = useState("Monthly"); // Monthly, Yearly, Daily

  const [selectedReport, setSelectedReport] = useState({
    key: "",
    data: [],
  });
  const [tableData, setTableData] = useState({
    "Registration reports": [],
    "Collection/invoice report": [],
    "Revenue by Associate (Employee)": [],
    "Contract report": [],
    "Client list- All clients": [],
    "Client list- Individual": [],
    "Client list- Business": [],
    [TIMESHEET_REPORT_KEY]: [],
  });
  console.log(selectedReport, STRUCTURE_JSON[selectedReport], "selectedReport");
  const {
    client: { overAllClients, businessClients, individualClients },
    invoice: { allInvoices, collectionInvoices },
    contract: { allContracts },
    employee: { activeEmployees },
    timesheet: { allTimesheets },
    master: { activity: activityMaster = [] },
  } = useSelector((state) => state);

  const [timesheetChartData, setTimesheetChartData] = useState([]);
  const [timesheetReportBulkRows, setTimesheetReportBulkRows] = useState([]);
  const [managers, setManagers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedManager, setSelectedManager] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [expandedTimesheetManagers, setExpandedTimesheetManagers] = useState(
    () => new Set(),
  );
  const [expandedRevenueManagers, setExpandedRevenueManagers] = useState(
    () => new Set(),
  );

  const managerIdForFilter = useMemo(
    () =>
      getManagerIdForReports(isManagerView, loggedManagerId, selectedManager),
    [isManagerView, loggedManagerId, selectedManager],
  );

  const managerKeyForTeam = useMemo(
    () =>
      getManagerKeyForEmployeeList(
        isManagerView,
        loggedManagerId,
        selectedManager,
      ),
    [isManagerView, loggedManagerId, selectedManager],
  );

  const timesheetAccordionRows = useMemo(() => {
    const flat = tableData[TIMESHEET_REPORT_KEY] || [];
    const grouped = buildManagerAccordionTimesheetRows(
      flat,
      activeEmployees || [],
    );
    return filterManagerAccordionRows(grouped, {
      isManagerView,
      loggedManagerId,
      selectedManager,
      selectedEmployee,
    });
  }, [
    tableData,
    activeEmployees,
    isManagerView,
    loggedManagerId,
    selectedManager,
    selectedEmployee,
  ]);

  const toggleTimesheetManager = (managerId) => {
    const key = String(managerId);
    setExpandedTimesheetManagers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const revenueAccordionRows = useMemo(() => {
    const flat = tableData[REVENUE_BY_EMPLOYEE_REPORT_KEY] || [];
    const grouped = buildManagerAccordionRevenueRows(
      flat,
      activeEmployees || [],
    );
    return filterManagerAccordionRows(grouped, {
      isManagerView,
      loggedManagerId,
      selectedManager,
      selectedEmployee,
    });
  }, [
    tableData,
    activeEmployees,
    isManagerView,
    loggedManagerId,
    selectedManager,
    selectedEmployee,
  ]);

  const toggleRevenueManager = (managerId) => {
    const key = String(managerId);
    setExpandedRevenueManagers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const formatRevenueAccordionCell = useCallback(
    (columnId, value, row) => {
      const openCountLink = (count, onOpen) => {
        if (count <= 0) return 0;
        return (
          <button
            type="button"
            className="reports-pending-invoices-link"
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
          >
            {count}
          </button>
        );
      };

      if (columnId === "num_pending_invoices") {
        return openCountLink(parseInt(value, 10) || 0, () =>
          openRevenuePendingInvoicesTab(row, revenueInvoiceFilters),
        );
      }
      if (columnId === "num_payments_done") {
        return openCountLink(parseInt(value, 10) || 0, () =>
          openRevenuePaidInvoicesTab(
            row,
            dateFilters["Revenue by Associate (Employee)"] ||
              revenueInvoiceFilters,
          ),
        );
      }
      if (columnId === "num_customers") {
        return openCountLink(parseInt(value, 10) || 0, () =>
          openRevenueCustomersTab(row),
        );
      }

      return formatRevenueCurrencyCell(columnId, value);
    },
    [dateFilters, revenueInvoiceFilters],
  );

  const teamScope = useMemo(
    () =>
      isManagerView
        ? buildTeamScope(activeEmployees, loggedManagerId)
        : { teamIds: null, teamNames: null },
    [isManagerView, activeEmployees, loggedManagerId],
  );

  const scopedOverAllClients = useMemo(
    () => filterForTeam(overAllClients, teamScope),
    [overAllClients, teamScope],
  );
  const scopedIndividualClients = useMemo(
    () => filterForTeam(individualClients, teamScope),
    [individualClients, teamScope],
  );
  const scopedBusinessClients = useMemo(
    () => filterForTeam(businessClients, teamScope),
    [teamScope, businessClients],
  );
  const scopedAllInvoices = useMemo(
    () => filterForTeam(allInvoices, teamScope),
    [allInvoices, teamScope],
  );
  //graph data - Bar chart data for Revenue
  const rechartsData = useMemo(() => {
    if (!lineChartData?.labels?.length) return [];

    return lineChartData.labels.map((name, index) => ({
      name,
      collected: parseAmount(lineChartData.datasets[0]?.data[index]) ?? 0,
      pending: parseAmount(lineChartData.datasets[1]?.data[index]) ?? 0,
    }));
  }, [lineChartData]);

  const invoiceAmountTotals = useMemo(() => {
    return (scopedAllInvoices || []).reduce(
      (acc, item) => {
        const { paidAmount, dueAmount } = getInvoiceAmounts(item);
        acc.paid += paidAmount;
        acc.pending += dueAmount;
        return acc;
      },
      { paid: 0, pending: 0 },
    );
  }, [scopedAllInvoices]);

  const revenueEmployeeTotals = useMemo(
    () => ({
      collected: invoiceAmountTotals.paid,
      pending: invoiceAmountTotals.pending,
    }),
    [invoiceAmountTotals],
  );

  // Chart data for Revenue tab - Monthly revenue trend
  useMemo(() => {
    if (!scopedAllInvoices || scopedAllInvoices.length === 0) return;

    const monthlyRevenue = {};
    scopedAllInvoices.forEach((invoice) => {
      const date = new Date(invoice.invoice_date);
      const monthKey = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}`;
      const monthName = date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      if (!monthlyRevenue[monthKey]) {
        monthlyRevenue[monthKey] = {
          month: monthName,
          collected: 0,
          pending: 0,
        };
      }
      const { paidAmount, dueAmount } = getInvoiceAmounts(invoice);
      monthlyRevenue[monthKey].collected += paidAmount;
      monthlyRevenue[monthKey].pending += dueAmount;
    });

    const chartData = Object.values(monthlyRevenue)
      .sort((a, b) => {
        const dateA = Object.keys(monthlyRevenue).find(
          (key) => monthlyRevenue[key] === a,
        );
        const dateB = Object.keys(monthlyRevenue).find(
          (key) => monthlyRevenue[key] === b,
        );
        return dateA.localeCompare(dateB);
      })
      .slice(-12); // Last 12 months
    setRevenueChartData(chartData);
  }, [scopedAllInvoices]);

  // Chart data for Clients tab - Client type distribution
  useMemo(() => {
    if (!scopedOverAllClients || scopedOverAllClients.length === 0) return;

    const typeCounts = scopedOverAllClients.reduce((acc, client) => {
      const type = client.client_type || "Unknown";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    const chartData = Object.entries(typeCounts).map(([name, value]) => ({
      name,
      value,
    }));
    setClientChartData(chartData);
  }, [scopedOverAllClients]);

  // Load Employee API and Revenue API on page load (landing dashboard)
  useEffect(() => {
    if (selectedReport.key) return;

    const initialFilters = {};
    if (revenueInvoiceFilters.start_date)
      initialFilters.start_date = revenueInvoiceFilters.start_date;
    if (revenueInvoiceFilters.end_date)
      initialFilters.end_date = revenueInvoiceFilters.end_date;
    const filters = appendManagerReportFilters(
      {
        employee_id: selectedEmployee !== "All" ? selectedEmployee : null,
        ...initialFilters,
      },
      isManagerView,
      managerIdForFilter,
      { revenueByEmployee: true },
    );
    dispatch(INVOICE_ACTION.getAllInvoices(filters));
  }, [
    dispatch,
    revenueInvoiceFilters,
    managerIdForFilter,
    selectedEmployee,
    selectedReport.key,
  ]);

  useEffect(() => {
    dispatch(EMPLOYEE_ACTION.getActiveEmployees());
  }, []);

  // Process employee data when API data is available
  useEffect(() => {
    if (!activeEmployees) return;
    if (isManagerView) return;

    // Get managers (role_id === 2)
    const managersList = activeEmployees
      .filter((emp) => emp.role_id === 2)
      .map((emp) => ({
        id: emp.employee_id,
        value: `${emp.first_name} ${emp.last_name}`,
      }));

    setManagers([{ id: "All", value: "All" }, ...managersList]);
    setEmployees([{ id: "All", value: "All" }]); // Reset employees list
  }, [activeEmployees]);

  // Filter employees when a manager is selected
  useEffect(() => {
    if (!activeEmployees) {
      setEmployees([{ id: "All", value: "All" }]);
      return;
    }

    if (!isManagerView && managerKeyForTeam === "All") {
      // Show all employees when "All" manager is selected (admin)
      const allEmployeesList = activeEmployees.map((emp) => ({
        id: emp.employee_id,
        value: `${emp.first_name} ${emp.last_name}`,
      }));
      setEmployees([{ id: "All", value: "All" }, ...allEmployeesList]);
      setSelectedEmployee("All");
      return;
    }

    // Full reporting tree under selected / logged-in manager
    const teamMemberIds = collectDescendantEmployeeIds(
      activeEmployees,
      managerKeyForTeam,
      { includeSelf: false },
    );
    const filteredEmployees = activeEmployees
      .filter((emp) => teamMemberIds.has(String(emp.employee_id)))
      .map((emp) => ({
        id: emp.employee_id,
        value: `${emp.first_name} ${emp.last_name}`,
      }));

    setEmployees([{ id: "All", value: "All" }, ...filteredEmployees]);
    setSelectedEmployee("All");
  }, [managerKeyForTeam, activeEmployees, isManagerView]);

  useEffect(() => {
    if (selectedReport.key !== "Timesheet") return;

    if (selectedEmployee !== "All" || selectedManager !== "All") {
      setExpandedTimesheetManagers(
        new Set(timesheetAccordionRows.map((row) => String(row.managerId))),
      );
      return;
    }

    if (isManagerView && loggedManagerId) {
      setExpandedTimesheetManagers(new Set([String(loggedManagerId)]));
      return;
    }

    setExpandedTimesheetManagers(new Set());
  }, [
    selectedReport.key,
    selectedEmployee,
    selectedManager,
    isManagerView,
    loggedManagerId,
    timesheetAccordionRows,
  ]);

  useEffect(() => {
    if (selectedReport.key) return;

    if (selectedEmployee !== "All" || selectedManager !== "All") {
      setExpandedRevenueManagers(
        new Set(revenueAccordionRows.map((row) => String(row.managerId))),
      );
      return;
    }

    if (isManagerView && loggedManagerId) {
      setExpandedRevenueManagers(new Set([String(loggedManagerId)]));
      return;
    }

    setExpandedRevenueManagers(new Set());
  }, [
    selectedReport.key,
    selectedEmployee,
    selectedManager,
    isManagerView,
    loggedManagerId,
    revenueAccordionRows,
  ]);

  useMemo(() => {
    if (scopedOverAllClients) {
      const registrationData = scopedOverAllClients.map((data) => {
        const {
          first_name,
          last_name,
          phone,
          email,
          employee,
          source,
          client_type,
          branch_office,
          status,
          createdAt,
          client_id,
        } = data;
        return {
          createdAt: dateFormatter(createdAt),
          // client_id,
          name: `${first_name} ${last_name}`,
          email,
          phone,
          employee,
          branch_office,
          client_type,
          status: status ? "Active " : "Inactive",
          source,
        };
      });
      setTableData((prev) => ({
        ...prev,
        "Registration reports": registrationData,
      }));

      // Generate chart data for Registration tab - Group by period (Monthly/Yearly/Daily)
      const processRegistrationData = (period) => {
        const groupedData = {};
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        // First, collect all registrations
        scopedOverAllClients.forEach((client) => {
          const date = new Date(client.createdAt);
          let key = "";

          if (period === "Monthly") {
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            key = `${month} ${year}`;
          } else if (period === "Yearly") {
            key = date.getFullYear().toString();
          } else if (period === "Daily") {
            key = dateFormatter(client.createdAt);
          }

          groupedData[key] = (groupedData[key] || 0) + 1;
        });

        if (period === "Monthly") {
          // Find the date range from the data
          const dates = scopedOverAllClients.map(
            (client) => new Date(client.createdAt),
          );
          if (dates.length === 0) return [];

          const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
          const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

          // Generate all months between min and max date
          const allMonths = [];
          const currentDate = new Date(minDate);
          currentDate.setDate(1); // Start from first day of month

          while (currentDate <= maxDate) {
            const month = monthNames[currentDate.getMonth()];
            const year = currentDate.getFullYear();
            const key = `${month} ${year}`;
            allMonths.push({
              name: key,
              count: groupedData[key] || 0,
            });

            // Move to next month
            currentDate.setMonth(currentDate.getMonth() + 1);
          }

          return allMonths;
        } else if (period === "Yearly") {
          // Find the year range
          const dates = scopedOverAllClients.map(
            (client) => new Date(client.createdAt),
          );
          if (dates.length === 0) return [];

          const minYear = Math.min(...dates.map((d) => d.getFullYear()));
          const maxYear = Math.max(...dates.map((d) => d.getFullYear()));

          // Generate all years between min and max
          const allYears = [];
          for (let year = minYear; year <= maxYear; year++) {
            allYears.push({
              name: year.toString(),
              count: groupedData[year.toString()] || 0,
            });
          }

          return allYears;
        } else {
          // For Daily, return as is (already sorted by date)
          return Object.entries(groupedData)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => new Date(a.name) - new Date(b.name));
        }
      };

      const chartData = processRegistrationData(registrationPeriod);
      setRegistrationChartData(chartData);
    }
  }, [scopedOverAllClients, registrationPeriod]);

  useMemo(() => {
    const collectionInvoices =
      collectionStatusFilter === "All"
        ? scopedAllInvoices
        : (scopedAllInvoices || []).filter(
            (invoice) =>
              getCollectionReportStatusLabel(invoice) === collectionStatusFilter,
          );

    const invoiceData = collectionInvoices.map((data) => {
      const {
        bill_to_first_name,
        bill_to_last_name,
        // bill_to_phone,
        bill_to_email,
        bill_from_first_name,
        bill_from_last_name,
        client_type,
        branch_office,
        status,
        invoice_no,
        total,
        invoice_date,
      } = data;
      const { paidAmount } = getInvoiceAmounts(data);
      return {
        createdAt: dateFormatter(invoice_date),
        bill_to_name: `${bill_to_first_name} ${bill_to_last_name}`,
        invoice_no,
        bill_to_email,
        // bill_to_phone,
        bill_from_name: `${bill_from_first_name} ${bill_from_last_name}`,
        branch_office: branch_office || "",
        client_type: client_type || "",
        total,
        paid_amount: paidAmount,
        pending_amount: getCollectionReportPendingAmount(data),
        status: getCollectionReportStatusLabel(data),
        paid_date:
          getInvoiceStatusLabel(status) === "Paid" ||
          getInvoiceStatusLabel(status) === "Partially Paid"
            ? formatInvoicePaidDateDisplay(data)
            : "—",
        payment_method: getInvoicePaymentMethodLabel(data),
      };
    });
    setTableData((prev) => ({
      ...prev,
      "Collection/invoice report": invoiceData,
    }));

    // Generate chart data for Invoice tab - count based bars, amount based tooltip
    const statusCounts = collectionInvoices.reduce((acc, invoice) => {
      const statusLabel = getCollectionReportStatusLabel(invoice);
      const { paidAmount, dueAmount, totalAmount } = getInvoiceAmounts(invoice);

      if (!acc[statusLabel]) {
        acc[statusLabel] = {
          name: statusLabel,
          value: 0,
          paidAmount: 0,
          dueAmount: 0,
          totalAmount: 0,
        };
      }

      acc[statusLabel].value += 1;
      acc[statusLabel].paidAmount += paidAmount;
      acc[statusLabel].dueAmount += dueAmount;
      acc[statusLabel].totalAmount += totalAmount;
      return acc;
    }, {});

    const overallAmounts = collectionInvoices.reduce(
      (acc, invoice) => {
        if (getCollectionReportStatusLabel(invoice) === "Cancelled") return acc;
        const { paidAmount, dueAmount } = getInvoiceAmounts(invoice);
        acc.paid += paidAmount;
        acc.pending += dueAmount;
        return acc;
      },
      { paid: 0, pending: 0 },
    );

    const partialPaidAmount = statusCounts["Partially Paid"]?.paidAmount || 0;
    const partialPendingAmount = statusCounts["Partially Paid"]?.dueAmount || 0;

    const chartData = [
      {
        name: "Paid",
        value: statusCounts["Paid"]?.value || 0,
        displayPaidAmount: overallAmounts.paid,
      },
      {
        name: "Partially Paid",
        value: statusCounts["Partially Paid"]?.value || 0,
        partialPaidAmount,
        partialPendingAmount,
      },
      {
        name: "Pending",
        value: statusCounts["Pending"]?.value || 0,
        displayPendingAmount: overallAmounts.pending,
      },
      {
        name: "Cancelled",
        value: statusCounts["Cancelled"]?.value || 0,
        displayCancelledAmount: statusCounts["Cancelled"]?.totalAmount || 0,
      },
    ].filter((item) => item.value > 0);

    setInvoiceChartData(chartData);
  }, [scopedAllInvoices, collectionStatusFilter]);

  // const triggerExport = () => {
  //   const excelBtn = document.querySelector(".export-file-format");

  //   if (excelBtn) {
  //     excelBtn.click();
  //     console.log("Excel export triggered!");
  //   } else {
  //     console.log("Export button NOT found!");
  //   }
  // };

  useMemo(() => {
    if (!scopedAllInvoices) return;

    const employeeRevenueData = scopedAllInvoices.map((data) => {
      const {
        employee_name,
        employee_id,
        sales,
        billed_by,
        num_customers,
        num_payments_done,
        num_pending_invoices,
        amount_collected,
        amount_pending,
      } = data;

      const collectedAmount = parseAmount(amount_collected) ?? 0;
      const pendingAmount = parseAmount(amount_pending) ?? 0;

      return {
        employee_id: employee_id ?? sales ?? billed_by ?? null,
        employee_name,
        num_customers,
        num_payments_done,
        num_pending_invoices,
        amount_collected: collectedAmount.toFixed(2),
        amount_pending: pendingAmount.toFixed(2),
      };
    });

    setTableData((prev) => ({
      ...prev,
      [REVENUE_BY_EMPLOYEE_REPORT_KEY]: employeeRevenueData,
    }));

    const lineData = {
      labels: employeeRevenueData.map((data) => data.employee_name),
      datasets: [
        {
          label: "Amount Paid",
          data: employeeRevenueData.map((data) => data.amount_collected),
          fill: false,
          borderColor: "rgb(75, 192, 192)",
          tension: 0.1,
        },
        {
          label: "Amount Pending",
          data: employeeRevenueData.map((data) => data.amount_pending),
          fill: false,
          borderColor: "rgb(255, 99, 132)",
          tension: 0.1,
        },
      ],
    };
    setLineChartData(lineData);

    const pieData1 = {
      labels: employeeRevenueData.map((data) => data.employee_name),
      datasets: [
        {
          data: employeeRevenueData.map((data) => data.num_customers),
          backgroundColor: [
            "rgb(75, 192, 192)",
            "rgb(255, 159, 64)",
            "rgb(153, 102, 255)",
            "rgb(54, 162, 235)",
            "rgb(255, 99, 132)",
          ],
        },
      ],
    };
    setPieChartData1(pieData1);

    const pieData2 = {
      labels: ["Payments Done", "Pending Invoices"],
      datasets: [
        {
          data: employeeRevenueData.reduce(
            (acc, data) => {
              acc[0] += data.num_payments_done;
              acc[1] += data.num_pending_invoices;
              return acc;
            },
            [0, 0],
          ),
          backgroundColor: ["rgb(75, 192, 192)", "rgb(255, 99, 132)"],
        },
      ],
    };
    setPieChartData2(pieData2);
  }, [scopedAllInvoices]);

  useMemo(() => {
    const invoiceData = scopedAllInvoices.map((data) => {
      const {
        bill_to_first_name,
        bill_to_last_name,
        bill_to_phone,
        bill_to_email,
        bill_from_first_name,
        bill_from_last_name,
        client_type,
        branch_office,
        status,
        invoice_no,
        total,
        invoice_date,
      } = data;
      return {
        createdAt: dateFormatter(invoice_date),
        bill_to_name: `${bill_to_first_name} ${bill_to_last_name}`,
        invoice_no,
        bill_to_email,
        // bill_to_phone,
        bill_from_name: `${bill_from_first_name} ${bill_from_last_name}`,
        branch_office: branch_office || "",
        client_type: client_type || "",
        total,
        status: status ? "Paid " : "Pending",
        // payment_date: "",
        // mode_of_payment: "",
      };
    });
    setTableData((prev) => ({
      ...prev,
      "Revenue comparison report- Monthly/Quarterly/yearly": invoiceData,
    }));
  }, [scopedAllInvoices]);

  // useMemo(() => {
  //   const invoiceData = allInvoices.map((data) => {
  //     const {
  //       bill_to_first_name,
  //       bill_to_last_name,
  //       bill_to_phone,
  //       bill_to_email,
  //       bill_from_first_name,
  //       bill_from_last_name,
  //       client_type,
  //       branch_office,
  //       status,
  //       client_id,
  //       invoice_no,
  //       total,
  //       invoice_date,
  //     } = data;
  //     return {
  //       createdAt: dateFormatter(invoice_date),
  //       client_id,
  //       bill_to_name: `${bill_to_first_name} ${bill_to_last_name}`,
  //       invoice_no,
  //       bill_to_email,
  //       bill_to_phone,
  //       bill_from_name: `${bill_from_first_name} ${bill_from_last_name}`,
  //       branch_office: branch_office || "",
  //       client_type: client_type || "",
  //       total,
  //       status: status ? "Paid " : "Pending",
  //       payment_date: "",
  //       mode_of_payment: "",
  //     };
  //   });
  //   setTableData((prev) => ({
  //     ...prev,
  //     "Revenue report- Location and employee/Associate wise": invoiceData,
  //   }));
  // }, [allInvoices]);

  useMemo(() => {
    const invoiceData = scopedOverAllClients.map((data) => {
      const {
        first_name,
        last_name,
        phone,
        email,
        employee,
        source,
        client_type,
        branch_office,
        status,
        createdAt,
      } = data;
      return {
        createdAt: dateFormatter(createdAt),
        name: `${first_name} ${last_name}`,
        email,
        phone,
        employee,
        branch_office,
        client_type,
        status: status ? "Active " : "Inactive",
        source,
      };
    });
    setTableData((prev) => ({
      ...prev,
      "Client list- All clients": invoiceData,
    }));
  }, [scopedOverAllClients]);

  useMemo(() => {
    const invoiceData = scopedIndividualClients.map((data) => {
      const {
        first_name,
        last_name,
        phone,
        email,
        employee,
        source,
        client_type,
        branch_office,
        status,
        createdAt,
      } = data;
      return {
        createdAt: dateFormatter(createdAt),
        name: `${first_name} ${last_name}`,
        email,
        phone,
        employee,
        branch_office,
        client_type,
        status: status ? "Active " : "Inactive",
        source,
      };
    });
    setTableData((prev) => ({
      ...prev,
      "Client list- Individual": invoiceData,
    }));
  }, [scopedIndividualClients]);

  useMemo(() => {
    const invoiceData = scopedBusinessClients.map((data) => {
      const {
        first_name,
        last_name,
        phone,
        email,
        employee,
        source,
        client_type,
        branch_office,
        status,
        createdAt,
      } = data;
      return {
        createdAt: dateFormatter(createdAt),
        name: `${first_name} ${last_name}`,
        email,
        phone,
        employee,
        branch_office,
        client_type,
        status: status ? "Active " : "Inactive",
        source,
      };
    });
    setTableData((prev) => ({
      ...prev,
      "Client list- Business": invoiceData,
    }));
  }, [scopedBusinessClients]);

  // useMemo(() => {
  //     const contractData = allContracts.map((data) => {
  //       const {
  //         bill_to_first_name, bill_to_last_name, bill_to_phone, bill_to_email,
  //         bill_from_first_name, bill_from_last_name, client_type, branch_office,
  //         status, client_id, contract_no, total, contract_date,
  //       } = data;
  //       return {
  //         createdAt: dateFormatter(contract_date),
  //         client_id,
  //         bill_to_name: `${bill_to_first_name} ${bill_to_last_name}`,
  //         contract_no,
  //         bill_to_email,
  //         bill_to_phone,
  //         bill_from_name: `${bill_from_first_name} ${bill_from_last_name}`,
  //         branch_office,
  //         client_type,
  //         total,
  //         status: status ? "Approved " : "Pending",
  //         approval_date: "",
  //       };
  //     });
  //     setTableData((prev) => ({
  //       ...prev,
  //       "Contract report": contractData,
  //     }));
  //   }, [allContracts]);

  const onChange = (value, key, label) => {
    if (label === "Registration reports") {
      setRevenueInvoiceFilters((prevFilters) => ({
        ...prevFilters,
        [key]: value ? value : null,
      }));
      // setRegistrationFilters(filters);
      dispatch(CLIENT_ACTION.getOverAllClients(revenueInvoiceFilters));
    }

    if (label === "Collection/invoice report") {
      const filters = {
        start_date: key === "start_date" ? value : invoiceFilters.start_date,
        end_date: key === "end_date" ? value : invoiceFilters.end_date,
      };
      setInvoiceFilters(filters);
      dispatch(
        INVOICE_ACTION.getAllInvoices(
          appendManagerReportFilters(filters, isManagerView, managerIdForFilter),
        ),
      );
    }

    if (label === "Revenue by Associate (Employee)") {
      const filters = appendManagerReportFilters(
        {
          start_date:
            key === "start_date" ? value : revenueInvoiceFilters.start_date,
          end_date: key === "end_date" ? value : revenueInvoiceFilters.end_date,
          employee_id: selectedEmployee !== "All" ? selectedEmployee : null,
        },
        isManagerView,
        getManagerIdForReports(
          isManagerView,
          loggedManagerId,
          selectedManager,
        ),
        { revenueByEmployee: true },
      );
      setRevenueInvoiceFilters(filters);
      dispatch(INVOICE_ACTION.getAllInvoices(filters));
    }

    // if (label === "Contract report") {
    //   const filters = {
    //     start_date: key === "start_date" ? value : contractFilters.start_date,
    //     end_date: key === "end_date" ? value : contractFilters.end_date,
    //   };
    //   setContractFilters(filters);
    //   dispatch(CONTRACT_ACTION.getAllContracts(filters));
    // }

    if (label === "Client list- All clients") {
      const filters = {
        start_date: key === "start_date" ? value : clientFiltersAll.start_date,
        end_date: key === "end_date" ? value : clientFiltersAll.end_date,
      };
      setClientFiltersAll(filters);
      dispatch(CLIENT_ACTION.getOverAllClients(filters));
    }

    if (label === "Client list- Individual") {
      const filters = {
        start_date:
          key === "start_date" ? value : clientFiltersIndividual.start_date,
        end_date: key === "end_date" ? value : clientFiltersIndividual.end_date,
      };
      setClientFiltersIndividual(filters);
      dispatch(CLIENT_ACTION.getIndividualClients(filters));
    }

    if (label === "Client list- Business") {
      const filters = {
        start_date:
          key === "start_date" ? value : clientFiltersBusiness.start_date,
        end_date: key === "end_date" ? value : clientFiltersBusiness.end_date,
      };
      setClientFiltersBusiness(filters);
      dispatch(CLIENT_ACTION.getBusinessClients(filters));
    }

    if (label === "Revenue comparison report- Monthly/Quarterly/yearly") {
      const filters = {
        start_date:
          key === "start_date" ? value : revenueTimeWiseFilters.start_date,
        end_date: key === "end_date" ? value : revenueTimeWiseFilters.end_date,
      };
      setRevenueTimeWiseFilter(filters);
      dispatch(
        INVOICE_ACTION.getAllInvoices(
          appendManagerReportFilters(filters, isManagerView, managerIdForFilter),
        ),
      );
    }
  };

  const reports = Object.keys(STRUCTURE_JSON).map((report_key) => ({
    id: report_key,
    value: report_key,
    icons: REPORT_CATEGORY_ICONS[report_key] || Timesheet,
  }));

  const onChangeData = (selectedId) => {
    const returnAPIS =
      STRUCTURE_JSON[selectedId]?.map((item) => item.API) || [];
    returnAPIS.forEach((DYNAMIC_API) => {
      DYNAMIC_API && dispatch(DYNAMIC_API());
    });
  };

  const onChangeReport = (selectedId) => {
    const reportData = STRUCTURE_JSON[selectedId] || [];

    setDateFilters((prev) => {
      const next = { ...prev };
      reportData.forEach((item) => {
        if (item.label) {
          next[item.label] = buildReportDateFilterState(item.label);
        }
      });
      return next;
    });

    setSelectedReport({
      key: selectedId,
      data: reportData,
    });

    reportData.forEach((item) => {
      if (!item.API) return;
      const reportKey = item.label;
      const dateFilter = buildReportDateFilterState(reportKey);
      let filters = appendManagerReportFilters(
        {},
        isManagerView,
        managerIdForFilter,
      );
      filters = appendReportDateFilters(filters, dateFilter);
      if (reportKey === "Revenue by Associate (Employee)") {
        Object.assign(
          filters,
          appendManagerReportFilters(
            {
              employee_id:
                selectedEmployee !== "All" ? selectedEmployee : null,
            },
            isManagerView,
            getManagerIdForReports(
              isManagerView,
              loggedManagerId,
              selectedManager,
            ),
            { revenueByEmployee: true },
          ),
        );
      }
      if (reportKey === TIMESHEET_REPORT_KEY) {
        Object.assign(
          filters,
          appendManagerReportFilters(
            {
              employee_id:
                selectedEmployee !== "All" ? selectedEmployee : null,
            },
            isManagerView,
            getManagerIdForReports(
              isManagerView,
              loggedManagerId,
              selectedManager,
            ),
          ),
        );
      }
      dispatch(item.API(filters));
    });
  };

  useEffect(() => {
    if (!selectedReport?.key || !selectedReport.data?.length) return;

    setDateFilters((prev) => {
      const next = { ...prev };
      selectedReport.data.forEach((item) => {
        if (item.label) {
          next[item.label] = buildReportDateFilterState(item.label);
        }
      });
      return next;
    });
  }, [selectedReport.key]);

  console.log("selectedReport", selectedReport);
  const handleExcelExport = () => {
    const btn = document.querySelector(".export-file-format");
    if (btn) {
      btn.click();
    } else {
      console.error("Export button not found");
    }
  };

  const handleManualDateChange = (reportKey, key, value, API) => {
    let start = dateFilters[reportKey]?.start_date;
    let end = dateFilters[reportKey]?.end_date;

    if (key === "start_date") start = value;
    if (key === "end_date") end = value;

    const normalize = (d) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    };

    const today = normalize(new Date());

    // ✅ Block future dates
    if (start && normalize(start) > today) return;
    if (end && normalize(end) > today) return;

    // ✅ Block end < start
    if (start && end && normalize(end) < normalize(start)) return;

    // ✅ Update UI
    setDateFilters((prev) => ({
      ...prev,
      [reportKey]: {
        ...prev[reportKey],
        start_date: start,
        end_date: end,
        range: "Custom",
      },
    }));

    // ✅ Send EXACT selected dates to backend
    const filters = {};

    if (start) {
      filters.start_date = normalize(start).toISOString().split("T")[0];
    }

    if (end) {
      filters.end_date = normalize(end).toISOString().split("T")[0];
    }

    if (reportKey === "Revenue by Associate (Employee)") {
      Object.assign(
        filters,
        appendManagerReportFilters(
          {
            employee_id: selectedEmployee !== "All" ? selectedEmployee : null,
          },
          isManagerView,
          getManagerIdForReports(
            isManagerView,
            loggedManagerId,
            selectedManager,
          ),
          { revenueByEmployee: true },
        ),
      );
    }
    if (reportKey === TIMESHEET_REPORT_KEY) {
      Object.assign(
        filters,
        appendManagerReportFilters(
          {
            employee_id: selectedEmployee !== "All" ? selectedEmployee : null,
          },
          isManagerView,
          getManagerIdForReports(
            isManagerView,
            loggedManagerId,
            selectedManager,
          ),
        ),
      );
    }

    dispatch(API(filters));
  };

  const buildTimesheetReportFilters = useCallback(() => {
    const df = dateFilters[TIMESHEET_REPORT_KEY] || {};
    const filters = {};
    if (df.start_date) filters.start_date = df.start_date;
    if (df.end_date) filters.end_date = df.end_date;
    if (selectedEmployee !== "All") filters.employee_id = selectedEmployee;
    return appendManagerReportFilters(
      filters,
      isManagerView,
      managerIdForFilter,
    );
  }, [
    dateFilters,
    selectedEmployee,
    isManagerView,
    managerIdForFilter,
  ]);

  const applyTimesheetReportRows = (rows) => {
    const tableRows = rows.map(({ _chart, ...rest }) => rest);
    setTableData((prev) => ({
      ...prev,
      [TIMESHEET_REPORT_KEY]: tableRows,
    }));
    setTimesheetChartData(rows.map((r) => r._chart).filter(Boolean));
  };

  const filterTimesheetsForReport = useCallback(
    (timesheets) => {
      let filtered = filterTimesheetsByManager(
        timesheets || [],
        managerKeyForTeam,
        activeEmployees,
      );
      if (selectedEmployee !== "All") {
        filtered = filtered.filter(
          (ts) => String(ts.employee_id) === String(selectedEmployee),
        );
      }
      return filtered;
    },
    [managerKeyForTeam, activeEmployees, selectedEmployee],
  );

  const loadTimesheetReportData = useCallback(async () => {
    const filters = buildTimesheetReportFilters();

    const apiResult = await dispatch(
      TIMESHEET_ACTION.getTimesheetReport(filters),
    ).unwrap();

    if (Array.isArray(apiResult) && apiResult.length > 0) {
      setTimesheetReportBulkRows([]);
      applyTimesheetReportRows(mapApiTimesheetReportRows(apiResult));
      return;
    }

    const bulk = await dispatch(
      TIMESHEET_ACTION.getTimesheetDetailsBulk({
        ...filters,
        include_saved: true,
      }),
    ).unwrap();

    const filtered = filterTimesheetsForReport(bulk);
    setTimesheetReportBulkRows(filtered);
    if (!filtered.length) {
      applyTimesheetReportRows([]);
    }
  }, [
    buildTimesheetReportFilters,
    dispatch,
    filterTimesheetsForReport,
  ]);

  useEffect(() => {
    if (selectedReport.key !== "Timesheet") return;
    loadTimesheetReportData();
  }, [
    selectedReport.key,
    dateFilters[TIMESHEET_REPORT_KEY]?.range,
    dateFilters[TIMESHEET_REPORT_KEY]?.start_date,
    dateFilters[TIMESHEET_REPORT_KEY]?.end_date,
    managerIdForFilter,
    managerKeyForTeam,
    selectedEmployee,
    loadTimesheetReportData,
  ]);

  useEffect(() => {
    if (selectedReport.key !== "Timesheet") return;
    if (!timesheetReportBulkRows.length) return;

    applyTimesheetReportRows(
      aggregateTimesheetDetails(
        timesheetReportBulkRows,
        {},
        activeEmployees,
        activityMaster,
      ),
    );
  }, [
    selectedReport.key,
    timesheetReportBulkRows,
    activeEmployees,
    activityMaster,
  ]);

  useEffect(() => {
    if (!selectedReport.key) return;
    dispatch(
      INVOICE_ACTION.getAllInvoices(
        appendManagerReportFilters(
          {
            employee_id:
              selectedEmployee !== "All" ? selectedEmployee : null,
          },
          isManagerView,
          managerIdForFilter,
          { revenueByEmployee: true },
        ),
      ),
    );
  }, [
    selectedReport.key,
    managerIdForFilter,
    selectedEmployee,
    dispatch,
    isManagerView,
  ]);

  return (
    <div className="container reports-page">
      <div className="reports-sticky-header">
        {selectedReport.key && (
          <div className="reports-home-breadcrumb">
            <span
              className="TitlePane reports-home-link"
              onClick={() => {
                setSelectedReport({ key: "", data: [] });

                dispatch(
                  INVOICE_ACTION.getAllInvoices(
                    appendManagerReportFilters(
                      {
                        employee_id:
                          selectedEmployee !== "All" ? selectedEmployee : null,
                      },
                      isManagerView,
                      managerIdForFilter,
                      { revenueByEmployee: true },
                    ),
                  ),
                );
              }}
            >
              Home
            </span>
          </div>
        )}

        <div className="report-tiles">
          {reports?.map((option) => (
            <div
              key={option.id}
              className={`notify-tile ${
                option.id === selectedReport.key ? "active" : ""
              }`}
              onClick={() => onChangeReport(option.id)}
            >
              <Avatar src={option.icons} className="notify-avatar" />
              <div className="notify-message">{option.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="reports-page-body">
      {selectedReport.data.length > 0 ? (
        <Grid container spacing={3} className="paddingTop">
          {/* Registration Graph Section - Above all tables for Sign Up / Registration tab */}
          {selectedReport.key === "Sign Up / Registration" &&
            registrationChartData.length > 0 && (
              <Grid item xs={12}>
                <Card
                  style={{
                    borderRadius: "16px",
                    boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    border: "1px solid rgba(28, 116, 188, 0.1)",
                  }}
                >
                  <CardContent style={{ padding: "32px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="h6"
                        style={{
                          fontWeight: 700,
                          color: "#1C74BC",
                          fontSize: "22px",
                          letterSpacing: "-0.5px",
                          margin: 0,
                        }}
                      >
                        Total Registration / Sign up
                      </Typography>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {["Monthly", "Yearly"].map((period) => (
                          <button
                            key={period}
                            onClick={() => setRegistrationPeriod(period)}
                            style={{
                              padding: "8px 16px",
                              borderRadius: "8px",
                              border:
                                registrationPeriod === period
                                  ? "2px solid #1C74BC"
                                  : "2px solid #e2e8f0",
                              backgroundColor:
                                registrationPeriod === period
                                  ? "#1C74BC"
                                  : "#ffffff",
                              color:
                                registrationPeriod === period
                                  ? "#ffffff"
                                  : "#64748b",
                              fontWeight: 600,
                              fontSize: "14px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              minWidth: "80px",
                              boxSizing: "border-box",
                            }}
                          >
                            {period}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ width: "100%", height: "450px" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={registrationChartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <defs>
                            <linearGradient
                              id="registrationAreaGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#22c55e"
                                stopOpacity={0.8}
                              />
                              <stop
                                offset="50%"
                                stopColor="#22c55e"
                                stopOpacity={0.4}
                              />
                              <stop
                                offset="95%"
                                stopColor="#22c55e"
                                stopOpacity={0.1}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            opacity={0.15}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="name"
                            tick={{
                              fontSize: 12,
                              fill: "#64748b",
                              fontWeight: 500,
                            }}
                            axisLine={{ stroke: "#e2e8f0" }}
                            tickLine={{ stroke: "#e2e8f0" }}
                          />
                          <YAxis
                            tick={{
                              fontSize: 12,
                              fill: "#64748b",
                              fontWeight: 500,
                            }}
                            axisLine={{ stroke: "#e2e8f0" }}
                            tickLine={{ stroke: "#e2e8f0" }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#ffffff",
                              border: "none",
                              borderRadius: "12px",
                              boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
                              padding: "12px 16px",
                            }}
                            formatter={(value) => [`${value}`, "Total Sign up"]}
                            labelFormatter={(label) => label}
                          />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="count"
                            stroke="#22c55e"
                            strokeWidth={3}
                            fill="url(#registrationAreaGradient)"
                            dot={{
                              fill: "#22c55e",
                              r: 4,
                              strokeWidth: 2,
                              stroke: "#fff",
                            }}
                            activeDot={{
                              r: 7,
                              stroke: "#22c55e",
                              strokeWidth: 2,
                              fill: "#fff",
                            }}
                            name="Total Sign up"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            )}

          {/* Client Graph Section - Above all tables for Clients tab */}
          {selectedReport.key === "Clients" && clientChartData.length > 0 && (
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <Card
                    style={{
                      borderRadius: "16px",
                      boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
                      border: "1px solid rgba(245, 158, 11, 0.1)",
                    }}
                  >
                    <CardContent style={{ padding: "32px" }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        style={{
                          fontWeight: 700,
                          color: "#d97706",
                          marginBottom: "8px",
                          fontSize: "22px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        👥 Client Type Distribution
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          color: "#64748b",
                          marginBottom: "28px",
                          fontSize: "14px",
                        }}
                      >
                        Breakdown of clients by type
                      </Typography>
                      <div style={{ width: "100%", height: "450px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <defs>
                              <linearGradient
                                id="businessGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor="#1C74BC"
                                  stopOpacity={0.9}
                                />
                                <stop
                                  offset="95%"
                                  stopColor="#0ea5e9"
                                  stopOpacity={0.6}
                                />
                              </linearGradient>
                              <linearGradient
                                id="individualGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor="#f59e0b"
                                  stopOpacity={0.9}
                                />
                                <stop
                                  offset="95%"
                                  stopColor="#d97706"
                                  stopOpacity={0.6}
                                />
                              </linearGradient>
                              <linearGradient
                                id="unknownGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor="#8b5cf6"
                                  stopOpacity={0.9}
                                />
                                <stop
                                  offset="95%"
                                  stopColor="#a78bfa"
                                  stopOpacity={0.6}
                                />
                              </linearGradient>
                            </defs>
                            <Pie
                              data={clientChartData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent, value }) =>
                                `${name}\n${(percent * 100).toFixed(
                                  1,
                                )}% (${value})`
                              }
                              outerRadius={140}
                              innerRadius={60}
                              fill="#8884d8"
                              dataKey="value"
                              stroke="#ffffff"
                              strokeWidth={3}
                            >
                              {clientChartData.map((entry, index) => {
                                const isBusiness = entry.name
                                  .toLowerCase()
                                  .includes("business");
                                const isUnknown = entry.name
                                  .toLowerCase()
                                  .includes("unknown");
                                return (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      isBusiness
                                        ? "url(#businessGradient)"
                                        : isUnknown
                                          ? "url(#unknownGradient)"
                                          : "url(#individualGradient)"
                                    }
                                  />
                                );
                              })}
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#ffffff",
                                border: "none",
                                borderRadius: "12px",
                                boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
                                padding: "12px 16px",
                              }}
                              formatter={(value) => [`${value} clients`, ""]}
                            />
                            <Legend
                              wrapperStyle={{ paddingTop: "24px" }}
                              iconType="circle"
                              iconSize={12}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Card
                    style={{
                      borderRadius: "16px",
                      boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                      border: "1px solid rgba(28, 116, 188, 0.1)",
                    }}
                  >
                    <CardContent
                      style={{
                        padding: "32px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        height: "604px",
                      }}
                    >
                      <Typography
                        variant="h6"
                        gutterBottom
                        style={{
                          fontWeight: 700,
                          color: "#1C74BC",
                          marginBottom: "24px",
                          fontSize: "20px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        📊 Client Insights
                      </Typography>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "20px",
                          marginTop: "auto",
                          marginBottom: "auto",
                        }}
                      >
                        {clientChartData.map((item, index) => {
                          const total = clientChartData.reduce(
                            (sum, d) => sum + d.value,
                            0,
                          );
                          const percentage = (
                            (item.value / total) *
                            100
                          ).toFixed(1);
                          const isBusiness = item.name
                            .toLowerCase()
                            .includes("business");
                          const isUnknown = item.name
                            .toLowerCase()
                            .includes("unknown");
                          return (
                            <div
                              key={index}
                              style={{
                                padding: "20px",
                                borderRadius: "12px",
                                background: isBusiness
                                  ? "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                                  : isUnknown
                                    ? "linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)"
                                    : "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                                border: `2px solid ${
                                  isBusiness
                                    ? "#1C74BC"
                                    : isUnknown
                                      ? "#8b5cf6"
                                      : "#f59e0b"
                                }`,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: "8px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: 700,
                                    color: isBusiness
                                      ? "#1C74BC"
                                      : isUnknown
                                        ? "#7c3aed"
                                        : "#d97706",
                                  }}
                                >
                                  {item.name}
                                </span>
                                <span
                                  style={{
                                    fontSize: "24px",
                                    fontWeight: 700,
                                    color: isBusiness
                                      ? "#1C74BC"
                                      : isUnknown
                                        ? "#7c3aed"
                                        : "#d97706",
                                  }}
                                >
                                  {item.value}
                                </span>
                              </div>
                              <div
                                style={{
                                  width: "100%",
                                  height: "8px",
                                  backgroundColor: isBusiness
                                    ? "rgba(28, 116, 188, 0.2)"
                                    : isUnknown
                                      ? "rgba(139, 92, 246, 0.2)"
                                      : "rgba(245, 158, 11, 0.2)",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  marginTop: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    width: `${percentage}%`,
                                    height: "100%",
                                    background: isBusiness
                                      ? "linear-gradient(90deg, #1C74BC 0%, #3b82f6 100%)"
                                      : isUnknown
                                        ? "linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%)"
                                        : "linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)",
                                    borderRadius: "4px",
                                    transition: "width 0.5s ease",
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  marginTop: "8px",
                                  fontSize: "13px",
                                  color: "#64748b",
                                  fontWeight: 500,
                                }}
                              >
                                {percentage}% of total clients
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          )}

          {selectedReport.data.map((report_data) => {
            const reportKey = report_data.label;
            const showTableTopExport =
              reportKey === "Collection/invoice report" ||
              reportKey ===
                "Revenue comparison report- Monthly/Quarterly/yearly";

            return (
              <Grid item xs={12} key={reportKey}>
                {/* PANEL */}
                <div className="employee-table-panel panel">
                  {/* TITLE */}
                  <div
                    className="panel-heading"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <h3 className="panel-title">{report_data.label}</h3>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        fontWeight: 600,
                        color: "#1b1b1b",
                      }}
                    >
                      {/* <MuiButton
                        variant="outlined"
                        size="small"
                        startIcon={<GetApp />}
                        onClick={() =>
                          exportReportTableToExcel(
                            report_data.headers,
                            tableData[reportKey] || [],
                            `${String(reportKey).replace(/\s+/g, "_").replace(/[<>:"/\\|?*]/g, "")}_${formattedDate}`
                          )
                        }
                        disabled={!(tableData[reportKey]?.length)}
                        style={{ color: "#1C74BC", borderColor: "#1C74BC" }}
                      >
                        Export to Excel
                      </MuiButton> */}
                    </div>
                  </div>

                  <div
                    className="reports-date-range"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                      marginBottom: 20,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* ✅ DATE RANGE */}
                    <div style={{ minWidth: "200px" }}>
                      <Labelbox
                        type="select"
                        labelname="Date Range"
                        value={
                          dateFilters[reportKey]?.range === "Custom"
                            ? "Custom"
                            : dateFilters[reportKey]?.range ||
                              getDateFilterPresetForReport(reportKey)
                        }
                        dropdown={dateRanges.map((range) => ({
                          id: range,
                          value: range,
                        }))}
                        changeData={(e) =>
                          handleDateRangeChange(e, reportKey, report_data.API)
                        }
                      />
                    </div>

                    {/* ✅ START DATE (SAME DESIGN AS DOJ) */}
                    <Labelbox
                      type="datepicker"
                      labelname="Start Date"
                      value={dateFilters[reportKey]?.start_date || ""}
                      changeData={(e) =>
                        handleManualDateChange(
                          reportKey,
                          "start_date",
                          e,
                          report_data.API,
                        )
                      }
                    />

                    {/* ✅ END DATE (SAME DESIGN AS DOJ) */}
                    <Labelbox
                      type="datepicker"
                      labelname="End Date"
                      value={dateFilters[reportKey]?.end_date || ""}
                      changeData={(e) =>
                        handleManualDateChange(
                          reportKey,
                          "end_date",
                          e,
                          report_data.API,
                        )
                      }
                    />

                    {reportKey === "Collection/invoice report" && (
                      <div style={{ minWidth: "200px" }}>
                        <Labelbox
                          type="select"
                          labelname="Invoice Status"
                          value={collectionStatusFilter}
                          dropdown={[
                            { id: "All", value: "All" },
                            { id: "Paid", value: "Paid" },
                            { id: "Partially Paid", value: "Partially Paid" },
                            { id: "Pending", value: "Pending" },
                            { id: "Cancelled", value: "Cancelled" },
                          ]}
                          changeData={(value) =>
                            setCollectionStatusFilter(value || "All")
                          }
                        />
                      </div>
                    )}

                    {reportKey === TIMESHEET_REPORT_KEY && (
                      <>
                        {!isManagerView && (
                        <div style={{ minWidth: "200px" }}>
                          <Labelbox
                            type="select"
                            labelname="Manager"
                            value={selectedManager}
                            dropdown={managers}
                            changeData={(e) => setSelectedManager(e)}
                          />
                        </div>
                        )}
                        <div style={{ minWidth: "200px" }}>
                          <Labelbox
                            type="select"
                            labelname="Employee"
                            value={selectedEmployee}
                            dropdown={employees}
                            changeData={(e) => setSelectedEmployee(e)}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {reportKey === TIMESHEET_REPORT_KEY &&
                    timesheetChartData.length > 0 && (
                      <Card
                        style={{
                          marginBottom: 24,
                          marginTop: 8,
                          borderRadius: "16px",
                          boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                          border: "1px solid rgba(28, 116, 188, 0.1)",
                        }}
                      >
                        <CardContent style={{ padding: "24px" }}>
                          <Typography
                            variant="h6"
                            style={{
                              fontWeight: 700,
                              color: "#1C74BC",
                              marginBottom: 16,
                            }}
                          >
                            Hours by employee (billable vs non-billable vs idle)
                          </Typography>
                          <div
                            className="timesheet-report-chart"
                            style={{
                              width: "100%",
                              height: Math.max(
                                360,
                                timesheetChartData.length * 36 + 96,
                              ),
                            }}
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                layout="vertical"
                                data={timesheetChartData}
                                margin={{
                                  top: 8,
                                  right: 24,
                                  left: 4,
                                  bottom: 8,
                                }}
                                barCategoryGap="28%"
                                barGap={0}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  horizontal={false}
                                  stroke="#e2e8f0"
                                  opacity={0.7}
                                />
                                <XAxis
                                  type="number"
                                  tick={{
                                    fontSize: 11,
                                    fill: "#64748b",
                                    fontWeight: 500,
                                  }}
                                  axisLine={{ stroke: "#e2e8f0" }}
                                  tickLine={{ stroke: "#e2e8f0" }}
                                  tickFormatter={(v) => `${v}h`}
                                  domain={[0, "auto"]}
                                  allowDecimals={false}
                                />
                                <YAxis
                                  type="category"
                                  dataKey="name"
                                  width={148}
                                  interval={0}
                                  tick={{
                                    fontSize: 11,
                                    fill: "#374151",
                                    fontWeight: 500,
                                  }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <Tooltip content={<CustomTimesheetTooltip />} />
                                <Legend
                                  verticalAlign="bottom"
                                  wrapperStyle={{ paddingTop: 20 }}
                                  iconType="square"
                                  iconSize={10}
                                />
                                <Bar
                                  dataKey="billable"
                                  name="Billable"
                                  stackId="hours"
                                  fill="#1C74BC"
                                  barSize={18}
                                />
                                <Bar
                                  dataKey="idle"
                                  name="Idle"
                                  stackId="hours"
                                  fill="#94a3b8"
                                  barSize={18}
                                />
                                <Bar
                                  dataKey="nonBillable"
                                  name="Non-Billable"
                                  stackId="hours"
                                  fill="#f59e0b"
                                  barSize={18}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                  {reportKey === "Collection/invoice report" &&
                    invoiceChartData.length > 0 && (
                      <Grid container>
                        <Grid xs={12} md={8}>
                          <Card
                            style={{
                              marginRight: 15,
                              marginBottom: "50px",
                              marginTop: "20px",
                              borderRadius: "16px",
                              boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                              background:
                                "linear-gradient(135deg, #ffffff 0%, #fef3f2 100%)",
                              border: "1px solid rgba(239, 68, 68, 0.1)",
                            }}
                          >
                            <CardContent style={{ padding: "32px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "12px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  gutterBottom
                                  style={{
                                    fontWeight: 700,
                                    color: "#dc2626",
                                    marginBottom: "8px",
                                    fontSize: "22px",
                                    letterSpacing: "-0.5px",
                                  }}
                                >
                                  💰 Invoice Status Overview
                                </Typography>
                              </div>
                              <Typography
                                variant="body2"
                                style={{
                                  color: "#64748b",
                                  marginBottom: "28px",
                                  fontSize: "14px",
                                }}
                              >
                                Distribution of paid, partially paid, pending and
                                cancelled invoices
                              </Typography>
                              <div style={{ width: "100%", height: "450px" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    data={invoiceChartData}
                                    margin={{
                                      top: 20,
                                      right: 30,
                                      left: 20,
                                      bottom: 20,
                                    }}
                                    barCategoryGap="30%"
                                  >
                                    <defs>
                                      <linearGradient
                                        id="paidGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        {/* <stop
                                          offset="0%"
                                          stopColor="#4ade80"
                                          stopOpacity={0.95}
                                        /> */}
                                        <stop
                                          offset="100%"
                                          stopColor="#22c55e"
                                          stopOpacity={0.9}
                                        />
                                      </linearGradient>
                                      <linearGradient
                                        id="pendingGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        {/* <stop
                                          offset="0%"
                                          stopColor="#f87171"
                                          stopOpacity={0.95}
                                        /> */}
                                        <stop
                                          offset="100%"
                                          stopColor="#ef4444"
                                          stopOpacity={0.9}
                                        />
                                      </linearGradient>
                                      <linearGradient
                                        id="partialGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="100%"
                                          stopColor="#3b82f6"
                                          stopOpacity={0.9}
                                        />
                                      </linearGradient>
                                      <linearGradient
                                        id="cancelledGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="100%"
                                          stopColor="#b42318"
                                          stopOpacity={0.9}
                                        />
                                      </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                      strokeDasharray="3 3"
                                      opacity={0.15}
                                      vertical={false}
                                    />
                                    <XAxis
                                      dataKey="name"
                                      tick={{
                                        fontSize: 12,
                                        fill: "#64748b",
                                        fontWeight: 500,
                                      }}
                                      axisLine={{ stroke: "#e2e8f0" }}
                                      tickLine={{ stroke: "#e2e8f0" }}
                                    />
                                    <YAxis
                                      tick={{
                                        fontSize: 12,
                                        fill: "#64748b",
                                        fontWeight: 500,
                                      }}
                                      axisLine={{ stroke: "#e2e8f0" }}
                                      tickLine={{ stroke: "#e2e8f0" }}
                                    />
                                    <Tooltip
                                      content={<CustomInvoiceTooltip />}
                                    />
                                    <Legend
                                      wrapperStyle={{ paddingTop: "24px" }}
                                      iconType="rect"
                                    />
                                    <Bar
                                      dataKey="value"
                                      radius={[12, 12, 0, 0]}
                                      name="Invoices"
                                    >
                                      {invoiceChartData.map((entry, index) => {
                                        const isCancelled =
                                          entry.name.toLowerCase() ===
                                          "cancelled";
                                        const isPartiallyPaid = entry.name
                                          .toLowerCase()
                                          .includes("partially");
                                        const isPaid = entry.name
                                          .toLowerCase()
                                          .includes("paid");
                                        return (
                                          <Cell
                                            key={`cell-${index}`}
                                            fill={
                                              isCancelled
                                                ? "url(#cancelledGradient)"
                                                : isPartiallyPaid
                                                  ? "url(#partialGradient)"
                                                  : isPaid
                                                    ? "url(#paidGradient)"
                                                    : "url(#pendingGradient)"
                                            }
                                          />
                                        );
                                      })}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid xs={12} md={4}>
                          <Card
                            style={{
                              marginTop: "20px",
                              borderRadius: "16px",
                              boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                              background:
                                "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                              border: "1px solid rgba(28, 116, 188, 0.1)",
                              height: "606px",
                            }}
                          >
                            <CardContent
                              style={{
                                padding: "32px",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                height: "100%",
                              }}
                            >
                              <Typography
                                variant="h6"
                                gutterBottom
                                style={{
                                  fontWeight: 700,
                                  color: "#1C74BC",
                                  marginBottom: "24px",
                                  fontSize: "20px",
                                  letterSpacing: "-0.5px",
                                }}
                              >
                                📊 Quick Stats
                              </Typography>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "20px",
                                  marginTop: "auto", // pushes it down
                                  marginBottom: "auto", // balanced centering
                                }}
                              >
                                {invoiceChartData.map((item, index) => {
                                  const isCancelled =
                                    item.name.toLowerCase() === "cancelled";
                                  const isPartiallyPaid = item.name
                                    .toLowerCase()
                                    .includes("partially");
                                  const isPaid =
                                    item.name.toLowerCase().includes("paid") &&
                                    !item.name
                                      .toLowerCase()
                                      .includes("partially");
                                  const total = invoiceChartData.reduce(
                                    (sum, d) => sum + d.value,
                                    0,
                                  );
                                  const percentage = (
                                    (item.value / total) *
                                    100
                                  ).toFixed(1);
                                  return (
                                    <div
                                      key={index}
                                      style={{
                                        padding: "16px",
                                        borderRadius: "12px",
                                        background: isCancelled
                                          ? "linear-gradient(135deg, #fef3f2 0%, #fecdca 100%)"
                                          : isPartiallyPaid
                                            ? "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                                            : isPaid
                                              ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                              : "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                                        border: `2px solid ${
                                          isCancelled
                                            ? "#b42318"
                                            : isPartiallyPaid
                                              ? "#3b82f6"
                                              : isPaid
                                                ? "#22c55e"
                                                : "#ef4444"
                                        }`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: isCancelled
                                              ? "#b42318"
                                              : isPartiallyPaid
                                                ? "#1d4ed8"
                                                : isPaid
                                                  ? "#16a34a"
                                                  : "#dc2626",
                                          }}
                                        >
                                          {item.name}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: "18px",
                                            fontWeight: 700,
                                            color: isCancelled
                                              ? "#b42318"
                                              : isPartiallyPaid
                                                ? "#1d4ed8"
                                                : isPaid
                                                  ? "#16a34a"
                                                  : "#dc2626",
                                          }}
                                        >
                                          {item.value}
                                        </span>
                                      </div>
                                      <div
                                        style={{
                                          marginTop: "8px",
                                          fontSize: "12px",
                                          color: "#64748b",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {percentage}% of total
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    )}

                  {reportKey ===
                    "Revenue comparison report- Monthly/Quarterly/yearly" &&
                    revenueChartData.length > 0 && (
                      <Grid
                        container
                        spacing={3}
                        style={{ marginBottom: "24px" }}
                      >
                        <Grid xs={12}>
                          <Card
                            style={{
                              marginTop: "20px",
                              marginBottom: "40px",
                              borderRadius: "16px",
                              boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                              background:
                                "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                              border: "1px solid rgba(34, 197, 94, 0.1)",
                            }}
                          >
                            <CardContent style={{ padding: "32px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "12px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <div>
                                  <Typography
                                    variant="h6"
                                    gutterBottom
                                    style={{
                                      fontWeight: 700,
                                      color: "#16a34a",
                                      marginBottom: "8px",
                                      fontSize: "22px",
                                      letterSpacing: "-0.5px",
                                    }}
                                  >
                                    💵 Monthly Revenue Analysis
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    style={{
                                      color: "#64748b",
                                      marginBottom: "8px",
                                      fontSize: "14px",
                                    }}
                                  >
                                    Track revenue collection and pending amounts
                                    by month
                                  </Typography>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "16px",
                                    flexWrap: "wrap",
                                    fontWeight: 600,
                                    // marginBottom: "18px",
                                  }}
                                >
                                  <span style={{ color: "#16a34a" }}>
                                    Total Paid:{" "}
                                    <span style={{ fontSize: "17px" }}>
                                      {formatCurrency(invoiceAmountTotals.paid)}
                                    </span>
                                  </span>
                                  <span style={{ color: "#dc2626" }}>
                                    Total Pending:
                                    <span style={{ fontSize: "17px" }}>
                                      {" "}
                                      {formatCurrency(
                                        invoiceAmountTotals.pending,
                                      )}
                                    </span>
                                  </span>
                                </div>
                              </div>

                              <div
                                style={{
                                  width: "100%",
                                  height: "450px",
                                  marginTop: "24px",
                                }}
                              >
                                <ResponsiveContainer width="100%" height="100%">
                                  <ComposedChart
                                    data={revenueChartData}
                                    margin={{
                                      top: 20,
                                      right: 30,
                                      left: 20,
                                      bottom: 20,
                                    }}
                                  >
                                    <defs>
                                      <linearGradient
                                        id="revenueCollectedGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        {/* <stop
                                          offset="0%"
                                          stopColor="#4ade80"
                                          stopOpacity={0.95}
                                        /> */}
                                        <stop
                                          offset="100%"
                                          stopColor="#22c55e"
                                          stopOpacity={0.9}
                                        />
                                      </linearGradient>
                                      <linearGradient
                                        id="revenuePendingGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        {/* <stop
                                          offset="0%"
                                          stopColor="#f87171"
                                          stopOpacity={0.95}
                                        /> */}
                                        <stop
                                          offset="100%"
                                          stopColor="#ef4444"
                                          stopOpacity={0.9}
                                        />
                                      </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                      strokeDasharray="3 3"
                                      opacity={0.15}
                                      vertical={false}
                                    />
                                    <XAxis
                                      dataKey="month"
                                      tick={{
                                        fontSize: 11,
                                        fill: "#64748b",
                                        fontWeight: 500,
                                      }}
                                      axisLine={{ stroke: "#e2e8f0" }}
                                      tickLine={{ stroke: "#e2e8f0" }}
                                    />
                                    <YAxis
                                      tick={{
                                        fontSize: 11,
                                        fill: "#64748b",
                                        fontWeight: 500,
                                      }}
                                      axisLine={{ stroke: "#e2e8f0" }}
                                      tickLine={{ stroke: "#e2e8f0" }}
                                      tickFormatter={(value) =>
                                        formatCompactCurrency(value)
                                      }
                                    />
                                    <Tooltip
                                      content={<CustomRevenueTooltip />}
                                    />
                                    <Legend
                                      wrapperStyle={{ paddingTop: "24px" }}
                                      iconType="rect"
                                    />
                                    <Bar
                                      dataKey="collected"
                                      fill="url(#revenueCollectedGradient)"
                                      name="Amount Paid"
                                      radius={[12, 12, 0, 0]}
                                    />
                                    <Bar
                                      dataKey="pending"
                                      fill="url(#revenuePendingGradient)"
                                      name="Amount Pending"
                                      radius={[12, 12, 0, 0]}
                                    />
                                  </ComposedChart>
                                </ResponsiveContainer>
                              </div>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    )}
                  {showTableTopExport && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: "12px",
                      }}
                    >
                      {reportKey === "Collection/invoice report" ? (
                        <MuiButton
                          variant="outlined"
                          size="small"
                          startIcon={<GetApp />}
                          onClick={() =>
                            exportCollectionInvoiceReport(
                              tableData[reportKey] || [],
                              `Collection_invoice_report_${formattedDate}`,
                            )
                          }
                          disabled={!(tableData[reportKey]?.length)}
                          style={{ color: "#1C74BC", borderColor: "#1C74BC" }}
                        >
                          Export to Excel
                        </MuiButton>
                      ) : null}
                      {/* <MuiButton
                        variant="outlined"
                        size="small"
                        startIcon={<GetApp />}
                        onClick={handleExcelExport}
                        style={{ color: "#1C74BC", borderColor: "#1C74BC" }}
                      >
                        Export to Excel
                      </MuiButton> */}
                    </div>
                  )}
                  <div className="client-details-invoices">
                    {reportKey === TIMESHEET_REPORT_KEY ? (
                      <ReportsManagerAccordionTable
                        rows={timesheetAccordionRows}
                        expandedManagerIds={expandedTimesheetManagers}
                        onToggleManager={toggleTimesheetManager}
                        columns={TIMESHEET_ACCORDION_COLUMNS}
                        emptyDescription="No timesheet data for client managers"
                      />
                    ) : (
                      <Table
                        showExporter={true}
                        showFilter={false}
                        variant="small"
                        title=""
                        header={report_data.headers}
                        data={tableData[reportKey] || []}
                        filters={null}
                        filterCallback={() => {}}
                        aligncss="dashboard-table"
                      />
                    )}
                  </div>
                </div>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <>
          <div className="reports-filter-tiles">
            <Grid container spacing={2}>
              {!isManagerView && (
                <Grid item xs={6} md={4}>
                  <Labelbox
                    type="select"
                    labelname="Client Manager"
                    value={selectedManager}
                    dropdown={managers}
                    changeData={(e) => setSelectedManager(e)}
                  />
                </Grid>
              )}
              <Grid item xs={6} md={isManagerView ? 6 : 4}>
                <Labelbox
                  type="select"
                  labelname="Employee"
                  value={selectedEmployee}
                  dropdown={employees}
                  changeData={(e) => setSelectedEmployee(e)}
                />
              </Grid>
              <Grid item xs={6} md={isManagerView ? 6 : 4}>
                <Labelbox
                  type="select"
                  labelname="Date Range"
                  value={
                    dateFilters["Revenue by Associate (Employee)"]?.range ||
                    DEFAULT_DATE_RANGE_PRESET
                  }
                  dropdown={dateRanges.map((range) => ({
                    id: range,
                    value: range,
                  }))}
                  changeData={(e) =>
                    handleDateRangeChange(
                      e,
                      "Revenue by Associate (Employee)",
                      INVOICE_ACTION.getAllInvoices,
                    )
                  }
                />
              </Grid>
            </Grid>
          </div>
          <Grid container spacing={3} className="paddingTop">
            <Grid item xs={12}>
            <Box style={{ paddingTop: 0 }}>
              <Typography
                variant="h4"
                gutterBottom
                style={{
                  fontWeight: 600,
                  color: "#1b1b1b",
                  marginBottom: "20px",
                  marginTop: "20px",
                  fontSize: "24px",
                }}
              >
                Revenue Reports
              </Typography>

        
             

              {/* Pie Charts in Next Row */}
              {/* <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Number of Customers per Employee
                      </Typography>
                      <div style={{ width: "100%", height: "250px" }}>
                        <Pie
                          data={pieChartData1}
                          options={{
                            responsive: true,
                            plugins: {
                              title: {
                                display: true,
                                text: "Number of Customers per Employee",
                              },
                              datalabels: {
                                display: true,
                                color: "#000",
                                font: {
                                  weight: "bold",
                                  size: 14,
                                },
                                formatter: (value, context) => {
                                  return value;
                                },
                              },
                            },
                          }}
                          plugins={[ChartDataLabels]}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Payments Done vs Pending Invoices
                      </Typography>
                      <div style={{ width: "100%", height: "250px" }}>
                        <Pie
                          data={pieChartData2}
                          options={{
                            responsive: true,
                            plugins: {
                              title: {
                                display: true,
                                text: "Payments Done vs Pending Invoices",
                              },
                              datalabels: {
                                display: true,
                                color: "#000",
                                font: {
                                  weight: "bold",
                                  size: 14,
                                },
                                formatter: (value, context) => {
                                  return value;
                                },
                              },
                            },
                          }}
                          plugins={[ChartDataLabels]}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid> */}
            </Box>
            <div
              className="employee-table-panel panel"
              style={{ marginTop: 24 }}
            >
              <div
                className="panel-heading"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <h3 className="panel-title">Revenue by Associate (Employee)</h3>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    fontWeight: 600,
                    color: "#1b1b1b",
                  }}
                >
                  {/* <MuiButton
                    variant="outlined"
                    size="small"
                    startIcon={<GetApp />}
                    onClick={() =>
                      exportReportTableToExcel(
                        [
                          { id: "1", label: "Employee Name" },
                          { id: "2", label: "No. of Customers" },
                          { id: "3", label: "No. of Payments Done" },
                          { id: "4", label: "No. of Pending Invoices" },
                          { id: "5", label: "Amount Paid" },
                          { id: "6", label: "Amount Pending" },
                        ],
                        tableData["Revenue by Associate (Employee)"] || [],
                        `Revenue_by_Associate_Employee_${formattedDate}`
                      )
                    }
                    disabled={
                      !(tableData["Revenue by Associate (Employee)"]?.length)
                    }
                    style={{ color: "#1C74BC", borderColor: "#1C74BC" }}
                  >
                    Export to Excel
                  </MuiButton> */}
                </div>
              </div>
              {/* <div className="report-top-bar" style={{ marginBottom: 24 }}>
                <div
                  className="report-actions"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    flexWrap: "wrap",
                  }}
                >
                  <Labelbox
                    type="datepicker"
                    labelname="Start Date"
                    value={revenueInvoiceFilters.start_date}
                    changeData={(e) =>
                      onChange(
                        e,
                        "start_date",
                        "Revenue by Associate (Employee)",
                      )
                    }
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <Labelbox
                      type="datepicker"
                      labelname="End Date"
                      value={revenueInvoiceFilters.end_date}
                      changeData={(e) =>
                        onChange(
                          e,
                          "end_date",
                          "Revenue by Associate (Employee)",
                        )
                      }
                    />

                     Download icon next to end date 
                    <div
                      className="download-inject-slot"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <DownloadIcon
                        onClick={handleExcelExport}
                        style={{
                          cursor: "pointer",
                          fontSize: 30,
                          color: "#1C74BC",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div> */}
              <ReportsManagerAccordionTable
                rows={revenueAccordionRows}
                expandedManagerIds={expandedRevenueManagers}
                onToggleManager={toggleRevenueManager}
                columns={REVENUE_ACCORDION_COLUMNS}
                emptyDescription="No revenue data for client managers"
                formatCell={formatRevenueAccordionCell}
              />
            </div>
             <Grid style={{marginTop: "20px"}} container spacing={4}>
                <Grid item xs={12}>
                  <Card
                    style={{
                      borderRadius: "16px",
                      boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                      border: "1px solid rgba(34, 197, 94, 0.1)",
                    }}
                  >
                    <CardContent style={{ padding: "32px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <Typography
                            variant="h6"
                            gutterBottom
                            style={{
                              fontWeight: 700,
                              color: "#16a34a",
                              marginBottom: "8px",
                              fontSize: "24px",
                              letterSpacing: "-0.5px",
                            }}
                          >
                            💰 Revenue by Employee
                          </Typography>
                          <Typography
                            variant="body2"
                            style={{
                              color: "#64748b",
                              marginBottom: "8px",
                              fontSize: "14px",
                            }}
                          >
                            Compare paid and pending amounts across employees
                          </Typography>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                            flexWrap: "wrap",
                            fontWeight: 600,
                          }}
                        >
                          <span style={{ color: "#16a34a" }}>
                            Total Paid:{" "}
                            <span style={{ fontSize: "17px" }}>
                              {formatCurrency(invoiceAmountTotals.paid)}
                            </span>
                          </span>
                          <span style={{ color: "#dc2626" }}>
                            Total Pending:
                            <span style={{ fontSize: "17px" }}>
                              {" "}
                              {formatCurrency(invoiceAmountTotals.pending)}
                            </span>
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          width: "100%",
                          height: "500px",
                          marginTop: "20px",
                        }}
                      >
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={rechartsData}
                            margin={{
                              top: 20,
                              right: 30,
                              left: 20,
                              bottom: 80,
                            }}
                            barCategoryGap="25%"
                          >
                            <defs>
                              <linearGradient
                                id="collectedGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                {/* <stop
                                  offset="0%"
                                  stopColor="#4ade80"
                                  stopOpacity={0.9}
                                /> */}
                                <stop
                                  offset="100%"
                                  stopColor="#22c55e"
                                  stopOpacity={0.9}
                                />
                              </linearGradient>

                              <linearGradient
                                id="pendingGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                {/* <stop
                                  offset="0%"
                                  stopColor="#f87171"
                                  stopOpacity={0.9}
                                /> */}
                                <stop
                                  offset="100%"
                                  stopColor="#ef4444"
                                  stopOpacity={0.9}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              opacity={0.15}
                              vertical={false}
                            />
                            <XAxis
                              dataKey="name"
                              tick={{
                                fontSize: 11,
                                fill: "#64748b",
                                fontWeight: 500,
                              }}
                              angle={-45}
                              tickMargin={10}
                              textAnchor="end"
                              height={100}
                              axisLine={{ stroke: "#e2e8f0" }}
                              tickLine={{ stroke: "#e2e8f0" }}
                            />
                            <YAxis
                              tick={{
                                fontSize: 11,
                                fill: "#64748b",
                                fontWeight: 500,
                              }}
                              axisLine={{ stroke: "#e2e8f0" }}
                              tickLine={{ stroke: "#e2e8f0" }}
                              tickFormatter={(value) =>
                                formatCompactCurrency(value)
                              }
                            />
                            <Tooltip content={<CustomRevenueTooltip />} />
                            <Legend
                              wrapperStyle={{ paddingTop: "24px" }}
                              iconType="rect"
                              iconSize={12}
                            />
                            <Bar
                              dataKey="collected"
                              fill="url(#collectedGradient)"
                              name="Amount Paid"
                              radius={[12, 12, 0, 0]}
                            />
                            <Bar
                              dataKey="pending"
                              fill="url(#pendingGradient)"
                              name="Amount Pending"
                              radius={[12, 12, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
          </Grid>
        </Grid>
      </>
    )}
      </div>
    </div>
  );
};

export default Reports;
